import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowRight, Check, Loader2, Hammer } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/supabaseClient';
import { upsertSettingsByKey, SETTINGS_KEYS } from '@/lib/settingsData';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const TRADES = [
  'Concrete', 'HVAC', 'Electrical', 'Plumbing', 'Roofing', 'Framing',
  'General Contracting', 'Remodeling', 'Landscaping', 'Other',
];
const CREW_SIZES = ['Just me', '2-3', '4-5', '6-10', '10+'];

// Setup-only: get the essentials, then land them on the dashboard where a
// self-guided "Getting Started" checklist takes it from here (add a client,
// build an estimate, try PQ) — at their own pace. No more estimate-first push.
const STEPS = ['Welcome', 'Business', 'Done'];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Business
  const [biz, setBiz] = useState({
    name: profile?.full_name ? '' : '', trade: '', crew_size: '', phone: '', address: '',
  });

  const finish = async () => {
    setBusy(true);
    try {
      await supabase.rpc('mark_onboarding_complete');
      await refreshProfile?.();
      navigate('/dashboard', { replace: true });
    } catch (e) {
      // Even if the stamp fails, don't trap them — let them into the app.
      navigate('/dashboard', { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const skipAll = async () => {
    await finish();
  };

  const saveBusiness = async () => {
    if (!biz.name.trim()) return toast.error('Business name is required.');
    if (!biz.trade) return toast.error('Pick your primary trade.');
    if (!biz.crew_size) return toast.error('Pick your crew size.');
    setBusy(true);
    try {
      await upsertSettingsByKey(SETTINGS_KEYS.COMPANY, {
        name: biz.name.trim(),
        primary_trade: biz.trade,
        crew_size: biz.crew_size,
        phone: biz.phone.trim(),
        address: biz.address.trim(),
      });
      setStep(3);
    } catch (e) {
      toast.error(e?.message || 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background grain-overlay">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary))]/40 to-transparent" />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg">
          {/* Step dots */}
          <div className="mb-5 flex items-center justify-center gap-2" data-testid="onboarding-steps">
            {STEPS.map((s, i) => (
              <span key={s} className={cn('h-1.5 rounded-full transition-all',
                i + 1 === step ? 'w-6 bg-[hsl(var(--primary))]'
                : i + 1 < step ? 'w-4 bg-[hsl(var(--primary))]/50' : 'w-4 bg-border')} />
            ))}
          </div>

          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] shadow-2xl shadow-black/40">
            <CardContent className="p-6 sm:p-8" data-testid={`onboarding-screen-${step}`}>

              {/* SCREEN 1 — Welcome */}
              {step === 1 ? (
                <div className="text-center">
                  <img src="/kocore-logo.png" alt="" className="h-24 w-auto mx-auto" />
                  <h1 className="mt-4 text-2xl font-semibold text-foreground">
                    Alright, you're in. Let's set this up.
                  </h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Just the basics about your shop — takes a minute. Everything
                    else you can explore at your own pace.
                  </p>
                  <Button onClick={() => setStep(2)}
                    className="mt-6 w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
                    data-testid="onboarding-start">
                    Let's go <ArrowRight size={14} className="ml-2" />
                  </Button>
                  <button type="button" onClick={skipAll}
                    className="mt-3 text-[11px] text-muted-foreground hover:text-foreground underline"
                    data-testid="onboarding-skip-all">
                    I'll explore on my own
                  </button>
                </div>
              ) : null}

              {/* SCREEN 2 — Business (required) */}
              {step === 2 ? (
                <div className="space-y-4">
                  <Header eyebrow="Your shop" title="Tell us about your business" />
                  <L label="Business name">
                    <Input value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })}
                      placeholder="Jane's Concrete & Remodel" className="bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="ob-biz-name" />
                  </L>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <L label="Primary trade">
                      <Select value={biz.trade} onValueChange={(v) => setBiz({ ...biz, trade: v })}>
                        <SelectTrigger className="bg-[hsl(var(--muted))] border-border" data-testid="ob-biz-trade">
                          <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-border">
                          {TRADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </L>
                    <L label="Crew size">
                      <Select value={biz.crew_size} onValueChange={(v) => setBiz({ ...biz, crew_size: v })}>
                        <SelectTrigger className="bg-[hsl(var(--muted))] border-border" data-testid="ob-biz-crew">
                          <SelectValue placeholder="Pick one" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--card))] border-border">
                          {CREW_SIZES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </L>
                  </div>
                  <L label="Business phone" hint="Optional">
                    <Input value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })}
                      placeholder="(555) 123-4567" className="bg-[hsl(var(--muted))] border-border text-foreground" />
                  </L>
                  <L label="Business address" hint="Optional — used to pre-fill job addresses later">
                    <Input value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })}
                      placeholder="123 Main St, Springfield" className="bg-[hsl(var(--muted))] border-border text-foreground" />
                  </L>
                  <Button onClick={saveBusiness} disabled={busy}
                    className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
                    data-testid="ob-biz-next">
                    {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : null} Save & continue
                  </Button>
                </div>
              ) : null}

              {/* SCREEN 3 — Done */}
              {step === 3 ? (
                <div className="text-center">
                  <div className="text-3xl">🔨</div>
                  <h1 className="mt-2 text-2xl font-semibold text-foreground">
                    You're set up. Take it at your own pace.
                  </h1>
                  <div className="mt-4 rounded-md border border-border bg-[hsl(var(--background))]/40 p-4 text-left text-sm text-muted-foreground space-y-1.5">
                    <div className="flex items-center justify-center gap-2 text-foreground mb-2 font-medium">
                      <Hammer size={14} className="text-[hsl(var(--primary))]" /> What's next
                    </div>
                    <p className="text-center">
                      Your dashboard has a <span className="text-foreground font-medium">Getting Started</span> checklist —
                      add a client, build an estimate, and try a PQ walkthrough
                      whenever you're ready. No rush, nothing forced.
                    </p>
                  </div>
                  <p className="mt-3 text-[12px] text-[hsl(var(--primary))]">
                    PQ Intelligence starts learning the moment you build your first estimate.
                  </p>
                  <Button onClick={finish} disabled={busy}
                    className="mt-5 w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
                    data-testid="onboarding-finish">
                    {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
                    Go to dashboard
                  </Button>
                </div>
              ) : null}

            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Header = ({ eyebrow, title, sub }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))]">{eyebrow}</div>
    <h2 className="mt-1 text-xl font-semibold text-foreground">{title}</h2>
    {sub ? <p className="mt-1 text-sm text-muted-foreground">{sub}</p> : null}
  </div>
);

const L = ({ label, hint, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
    <div className="mt-1">{children}</div>
    {hint ? <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p> : null}
  </div>
);
